import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, Button, Badge, Input, Select, Table } from '../components/Common';

const SEVERITY_ORDER = { Critical: 0, High: 1, Medium: 2, Low: 3, Info: 4 };

export default function Retest() {
  const { apiFetch, user, hasGroup } = useAuth();
  const navigate = useNavigate();
  const [findings, setFindings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [engagementFilter, setEngagementFilter] = useState('');
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [retestNotes, setRetestNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const isTester = hasGroup('Penetration Testers') || hasGroup('MSP Penetration Testers') || hasGroup('Super Admins');
  const isMspSide = user?.isMsp || hasGroup('Super Admins');

  const load = useCallback(async () => {
    try {
      const res = await apiFetch('/findings/all');
      const data = await res.json().catch(() => null);
      setFindings(Array.isArray(data) ? data : []);
    } catch { setFindings([]); }
    setLoading(false);
  }, [apiFetch]);

  useEffect(() => { load(); }, [load]);

  const retests = findings.filter(f => f.status === 'Retest');
  const engagements = [...new Map(
    retests.map(f => [f.engagement_id, f.engagement_name || f.engagement_type || 'Untitled engagement'])
  ).entries()].sort((a, b) => a[1].localeCompare(b[1]));

  const visible = retests
    .filter(f => !engagementFilter || f.engagement_id === engagementFilter)
    .filter(f => {
      if (!search) return true;
      const q = search.toLowerCase();
      return [f.title, f.engagement_name, f.client_name, f.severity]
        .filter(Boolean).join(' ').toLowerCase().includes(q);
    })
    .sort((a, b) => (SEVERITY_ORDER[a.severity] ?? 9) - (SEVERITY_ORDER[b.severity] ?? 9));

  const submitRetest = async (findingId, passed) => {
    if (!retestNotes.trim()) return alert('Retest notes are required.');
    setSubmitting(true);
    try {
      const res = await apiFetch(`/findings/${findingId}/retest-result`, {
        method: 'POST',
        body: JSON.stringify({ passed, notes: retestNotes })
      });
      if (res.ok) {
        setExpandedId(null);
        setRetestNotes('');
        load();
      } else {
        const data = await res.json().catch(() => ({}));
        alert(data.error || 'Failed to record retest result');
      }
    } catch {} finally { setSubmitting(false); }
  };

  const getDaysInRetest = (updatedAt) => {
    if (!updatedAt) return null;
    return Math.floor((Date.now() - new Date(updatedAt).getTime()) / 86400000);
  };

  const columns = [
    { key: 'severity', label: 'Severity', render: v => <Badge severity={v}>{v}</Badge> },
    { key: 'title', label: 'Vulnerability', render: v => <span style={{ fontWeight: 600 }}>{v}</span> },
    { key: 'cvss_score', label: 'CVSS', render: v => (v == null ? '—' : v) },
    ...(isMspSide ? [{ key: 'client_name', label: 'Client', render: v => v || '—' }] : []),
    { key: 'engagement_name', label: 'Engagement', render: (v, row) => (
      <span style={{ color: 'var(--primary)', cursor: 'pointer' }}
        onClick={e => { e.stopPropagation(); navigate(`/engagements/${row.engagement_id}`); }}>
        {v || row.engagement_type || 'Untitled'}
      </span>
    )},
    { key: 'updated_at', label: 'Days Waiting', render: v => {
      const days = getDaysInRetest(v);
      if (days === null) return '—';
      const color = days > 14 ? '#ef4444' : days > 7 ? '#f59e0b' : 'var(--text-secondary)';
      return <span style={{ fontWeight: days > 7 ? 600 : 400, color }}>{days}d</span>;
    }},
    ...(isTester ? [{ key: 'id', label: 'Actions', render: (v, row) => (
      <div style={{ display: 'flex', gap: 4 }}>
        <Button variant="primary" style={{ padding: '4px 10px', fontSize: 11, background: '#22c55e', borderColor: '#22c55e' }}
          onClick={e => { e.stopPropagation(); setExpandedId(expandedId === v ? null : v); setRetestNotes(''); }}>
          Pass
        </Button>
        <Button variant="danger" style={{ padding: '4px 10px', fontSize: 11 }}
          onClick={e => { e.stopPropagation(); setExpandedId(expandedId === v ? null : v); setRetestNotes(''); }}>
          Fail
        </Button>
      </div>
    )}] : []),
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700 }}>Retest Queue</h1>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>
            Vulnerabilities submitted for retest verification
          </div>
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{retests.length} awaiting retest</span>
          <Button variant="secondary" onClick={load} style={{ padding: '6px 14px', fontSize: 12 }}>Refresh</Button>
        </div>
      </div>

      <Card style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        <Input placeholder="Search vulnerabilities..." value={search} onChange={e => setSearch(e.target.value)}
          style={{ marginBottom: 0, flex: 2, minWidth: 200 }} />
        <Select label="" options={[{ value: '', label: `All engagements (${retests.length})` },
          ...engagements.map(([id, name]) => ({ value: id, label: name }))]}
          value={engagementFilter} onChange={e => setEngagementFilter(e.target.value)}
          style={{ marginBottom: 0, flex: 1, minWidth: 200 }} />
        {(engagementFilter || search) && (
          <button onClick={() => { setEngagementFilter(''); setSearch(''); }} style={{
            padding: '10px 16px', borderRadius: 'var(--radius)', border: '1px solid var(--border)',
            background: 'var(--bg-input)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13
          }}>Clear</button>
        )}
      </Card>

      {loading ? (
        <Card style={{ textAlign: 'center', padding: 60, color: 'var(--text-secondary)' }}>Loading retest queue...</Card>
      ) : visible.length === 0 ? (
        <Card style={{ textAlign: 'center', padding: 60, color: 'var(--text-secondary)' }}>
          {retests.length === 0 ? 'Nothing is waiting to be retested.' : 'No vulnerabilities match this filter.'}
        </Card>
      ) : (
        <Card>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--bg-input)' }}>
                {columns.map(c => (
                  <th key={c.key} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-secondary)' }}>{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.map(row => (
                <React.Fragment key={row.id}>
                  <tr style={{ borderTop: '1px solid var(--border)', cursor: 'pointer' }}
                    onClick={() => navigate(`/engagements/${row.engagement_id}`)}>
                    {columns.map(c => (
                      <td key={c.key} style={{ padding: '10px 14px' }}>
                        {c.render ? c.render(row[c.key], row) : row[c.key]}
                      </td>
                    ))}
                  </tr>
                  {expandedId === row.id && isTester && (
                    <tr><td colSpan={columns.length} style={{ padding: '12px 14px', background: 'var(--bg)', borderTop: '1px solid var(--border)' }}>
                      <div style={{ maxWidth: 600 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>Retest Notes *</div>
                        <textarea value={retestNotes} onChange={e => setRetestNotes(e.target.value)}
                          placeholder="Describe your retest findings — what you tested, what you observed..."
                          rows={3} style={{
                            width: '100%', padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 'var(--radius)',
                            fontSize: 13, background: 'var(--bg-input)', color: 'var(--text)', fontFamily: 'inherit',
                            resize: 'vertical', boxSizing: 'border-box', marginBottom: 8
                          }} />
                        <div style={{ display: 'flex', gap: 8 }}>
                          <Button style={{ padding: '6px 16px', fontSize: 12, background: '#22c55e', borderColor: '#22c55e' }}
                            onClick={() => submitRetest(row.id, true)} disabled={submitting}>
                            {submitting ? '...' : 'Confirm Fix (Close)'}
                          </Button>
                          <Button variant="danger" style={{ padding: '6px 16px', fontSize: 12 }}
                            onClick={() => submitRetest(row.id, false)} disabled={submitting}>
                            {submitting ? '...' : 'Fix Failed (Reopen)'}
                          </Button>
                          <Button variant="secondary" style={{ padding: '6px 16px', fontSize: 12 }}
                            onClick={() => { setExpandedId(null); setRetestNotes(''); }}>
                            Cancel
                          </Button>
                        </div>
                      </div>
                    </td></tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
